import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { FileText, Plus, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

interface ReportePrueba {
  id: string;
  folioInterno: string;
  solicitudId: string;
  muestraId: string | null;
  muestraFolio: string | null;
  numeroReporte: string;
  laboratorioId: string;
  laboratorioRazonSocial: string;
  normaId: string;
  normaCodigo: string;
  fechaEmision: string;
  vigenciaInicio: string | null;
  vigenciaFin: string | null;
  vigente: boolean;
  resultado: string;
  productoDescripcion: string;
  marca: string;
  modelo: string;
  documentoId: string | null;
  estatus: string;
  observaciones: string | null;
  motivoRechazo: string | null;
  aclaraciones: string | null;
  fechaCaptura: string;
  fechaAceptacion: string | null;
  fechaRechazo: string | null;
  fechaObservacion: string | null;
  accionesDisponibles: string[];
}

interface LaboratorioDisponible {
  normaLaboratorioId: string;
  laboratorioId: string;
  laboratorioRazonSocial: string;
  acreditacionEma: string | null;
  normaId: string;
  normaCodigo: string;
}

interface Muestra {
  id: string;
  folio: string;
  estado: string;
}

interface Solicitud {
  marca: string;
  productoModelo: string;
  normaCodigo: string;
  estadoCodigo: string | null;
}

const ESTADOS_PERMITIDOS = new Set([
  "ALCANCE_TECNICO_DETERMINADO",
  "MUESTRA_REQUERIDA",
  "REPORTE_REQUERIDO",
  "EVALUACION_TECNICA",
  "ACLARACION_TECNICA",
  "INFORMACION_INCOMPLETA",
]);

const ESTATUS_COLOR: Record<string, string> = {
  CAPTURADO: "bg-estado-info-bg text-estado-info",
  OBSERVADO: "bg-estado-warn-bg text-estado-warn",
  ACEPTADO:  "bg-estado-ok-bg text-estado-ok",
  RECHAZADO: "bg-estado-err-bg text-estado-err",
};

const RESULTADO_COLOR: Record<string, string> = {
  CUMPLE:       "bg-estado-ok-bg text-estado-ok border-estado-ok/25",
  NO_CUMPLE:    "bg-estado-err-bg text-estado-err border-estado-err/25",
  CONDICIONADO: "bg-estado-warn-bg text-estado-warn border-estado-warn/25",
  INCONCLUSO:   "bg-estado-block-bg text-estado-block border-edge",
};

const ACCION_LABEL: Record<string, string> = {
  aceptar:              "Aceptar",
  rechazar:             "Rechazar",
  observar:             "Observar",
  subsanar:             "Subsanar",
  "vincular-documento": "Vincular PDF",
};

export function ReportesPruebaSection({
  solicitud,
  solicitudId,
}: {
  solicitud: Solicitud;
  solicitudId: string;
}) {
  const qc = useQueryClient();
  const [showAlta, setShowAlta] = useState(false);
  const [accionActiva, setAccionActiva] = useState<{ rp: ReportePrueba; accion: string } | null>(null);

  const lista = useQuery({
    queryKey: ["reportes-prueba", solicitudId],
    queryFn: async () =>
      (await api.get<ReportePrueba[]>(`/api/sector/telecom/solicitudes/${solicitudId}/reportes-prueba`)).data,
  });

  const puedeCapturar =
    !!solicitud.estadoCodigo && ESTADOS_PERMITIDOS.has(solicitud.estadoCodigo);

  return (
    <section className="rounded-lg border bg-surface p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-fg">
          <FileText className="mr-1 inline h-4 w-4" /> Reportes de Prueba
        </h2>
        <div className="flex items-center gap-2">
          {!puedeCapturar && (
            <span className="text-xs text-fg-subtle">
              {solicitud.estadoCodigo
                ? `Captura deshabilitada en ${solicitud.estadoCodigo}`
                : "Sin instancia de flujo"}
            </span>
          )}
          <button
            disabled={!puedeCapturar}
            onClick={() => setShowAlta(true)}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-2.5 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Capturar RP
          </button>
        </div>
      </div>

      {lista.isLoading && <p className="text-sm text-fg-subtle">Cargando…</p>}
      {lista.data && lista.data.length === 0 && (
        <p className="rounded border border-dashed p-4 text-center text-sm text-fg-subtle">
          Sin reportes de prueba para esta solicitud.
        </p>
      )}
      {lista.data && lista.data.length > 0 && (
        <div className="w-full overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-xs uppercase tracking-wide text-fg-muted">
            <tr>
              <th className="px-3 py-2">Folio</th>
              <th className="px-3 py-2">Laboratorio · número</th>
              <th className="px-3 py-2">Resultado</th>
              <th className="px-3 py-2">Vigencia</th>
              <th className="px-3 py-2">Estatus</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {lista.data.map((rp) => (
              <tr key={rp.id} className="align-top">
                <td className="px-3 py-2">
                  <div className="font-mono text-xs">{rp.folioInterno}</div>
                  {rp.muestraFolio && (
                    <div className="text-xs text-fg-subtle">Muestra: {rp.muestraFolio}</div>
                  )}
                  {rp.documentoId && (
                    <div className="mt-0.5 text-xs text-estado-ok">PDF vinculado</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium text-fg">{rp.laboratorioRazonSocial}</div>
                  <div className="text-xs text-fg-subtle">
                    {rp.normaCodigo} · {rp.numeroReporte}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded border px-2 py-0.5 text-xs font-medium ${
                      RESULTADO_COLOR[rp.resultado] ?? "bg-estado-block-bg text-estado-block border-edge"
                    }`}
                  >
                    {rp.resultado}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div>Emitido: {rp.fechaEmision}</div>
                  {rp.vigenciaFin ? (
                    <div className={rp.vigente ? "text-fg-muted" : "text-estado-err"}>
                      Vence: {rp.vigenciaFin} {!rp.vigente && "(vencido)"}
                    </div>
                  ) : (
                    <div className="text-fg-subtle">Sin fecha de vencimiento</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${
                      ESTATUS_COLOR[rp.estatus] ?? "bg-estado-block-bg text-estado-block"
                    }`}
                  >
                    {rp.estatus}
                  </span>
                  {rp.motivoRechazo && (
                    <div className="mt-1 text-xs text-estado-err">{rp.motivoRechazo}</div>
                  )}
                  {rp.observaciones && rp.estatus === "OBSERVADO" && (
                    <div className="mt-1 text-xs text-estado-warn">{rp.observaciones}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {rp.accionesDisponibles.length === 0 ? (
                    <span className="text-xs text-fg-faint">—</span>
                  ) : (
                    <div className="flex flex-wrap justify-end gap-1">
                      {rp.accionesDisponibles.map((a) => (
                        <button
                          key={a}
                          onClick={() => setAccionActiva({ rp, accion: a })}
                          className="cursor-pointer rounded border bg-surface px-2 py-0.5 text-xs transition hover:bg-surface-2"
                        >
                          {ACCION_LABEL[a] ?? a}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {showAlta && (
        <AltaModal
          solicitud={solicitud}
          solicitudId={solicitudId}
          onClose={() => setShowAlta(false)}
          onSuccess={() => {
            setShowAlta(false);
            qc.invalidateQueries({ queryKey: ["reportes-prueba", solicitudId] });
          }}
        />
      )}

      {accionActiva && (
        <AccionModal
          rp={accionActiva.rp}
          accion={accionActiva.accion}
          onClose={() => setAccionActiva(null)}
          onSuccess={() => {
            setAccionActiva(null);
            qc.invalidateQueries({ queryKey: ["reportes-prueba", solicitudId] });
          }}
        />
      )}
    </section>
  );
}

function AltaModal({
  solicitud,
  solicitudId,
  onClose,
  onSuccess,
}: {
  solicitud: Solicitud;
  solicitudId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const disponibles = useQuery({
    queryKey: ["rp-laboratorios", solicitudId],
    queryFn: async () =>
      (await api.get<LaboratorioDisponible[]>(
        `/api/sector/telecom/solicitudes/${solicitudId}/laboratorios-disponibles`,
      )).data,
  });

  const muestras = useQuery({
    queryKey: ["rp-muestras", solicitudId],
    queryFn: async () =>
      (await api.get<Muestra[]>(`/api/sector/telecom/solicitudes/${solicitudId}/muestras`)).data,
  });

  const [form, setForm] = useState({
    numeroReporte: "",
    parId: "",                         // value del select de laboratorio: normaLaboratorioId
    muestraId: "",
    fechaEmision: new Date().toISOString().slice(0, 10),
    vigenciaInicio: "",
    vigenciaFin: "",
    resultado: "",
    productoDescripcion: "",
    marca: solicitud.marca,
    modelo: solicitud.productoModelo,
    observaciones: "",
  });
  const [error, setError] = useState<string | null>(null);

  const parSeleccionado = useMemo(() => {
    return disponibles.data?.find((d) => d.normaLaboratorioId === form.parId) ?? null;
  }, [disponibles.data, form.parId]);

  const crear = useMutation({
    mutationFn: async () => {
      if (!parSeleccionado) {
        throw new AxiosError("Selecciona un laboratorio");
      }
      const body: Record<string, unknown> = {
        numeroReporte: form.numeroReporte.trim(),
        laboratorioId: parSeleccionado.laboratorioId,
        normaId: parSeleccionado.normaId,
        fechaEmision: form.fechaEmision,
        resultado: form.resultado,
        productoDescripcion: form.productoDescripcion.trim(),
        marca: form.marca.trim(),
        modelo: form.modelo.trim(),
      };
      if (form.muestraId) body.muestraId = form.muestraId;
      if (form.vigenciaInicio) body.vigenciaInicio = form.vigenciaInicio;
      if (form.vigenciaFin) body.vigenciaFin = form.vigenciaFin;
      if (form.observaciones.trim()) body.observaciones = form.observaciones.trim();
      await api.post(`/api/sector/telecom/solicitudes/${solicitudId}/reportes-prueba`, body);
    },
    onSuccess,
    onError: (err: AxiosError<{ detail?: string }>) =>
      setError(err.response?.data?.detail ?? "Error al capturar reporte"),
  });

  return (
    <Modal title="Capturar Reporte de Prueba" onClose={onClose}>
      <p className="text-xs text-fg-muted">
        Norma de la solicitud: <span className="font-mono">{solicitud.normaCodigo}</span>.
        El laboratorio debe estar autorizado para esa norma (RN-204). Marca y modelo del RP
        deben coincidir con los de la solicitud (RN-205).
      </p>

      <Field label="Número del reporte (asignado por el laboratorio) *">
        <input
          value={form.numeroReporte}
          onChange={(e) => setForm((f) => ({ ...f, numeroReporte: e.target.value }))}
          placeholder="Ej. LAB-RP-1234/2026"
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        />
      </Field>

      <Field label="Laboratorio acreditado *">
        {disponibles.isLoading && <p className="text-xs text-fg-subtle">Cargando laboratorios…</p>}
        {disponibles.data && disponibles.data.length === 0 && (
          <p className="rounded border border-estado-warn/25 bg-estado-warn-bg p-2 text-xs text-estado-warn">
            No hay laboratorios acreditados vigentes para {solicitud.normaCodigo}. Asigna primero
            uno desde la sección de laboratorio.
          </p>
        )}
        {disponibles.data && disponibles.data.length > 0 && (
          <select
            value={form.parId}
            onChange={(e) => setForm((f) => ({ ...f, parId: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          >
            <option value="">Selecciona…</option>
            {disponibles.data.map((d) => (
              <option key={d.normaLaboratorioId} value={d.normaLaboratorioId}>
                {d.laboratorioRazonSocial}
                {d.acreditacionEma ? ` — EMA ${d.acreditacionEma}` : ""}
              </option>
            ))}
          </select>
        )}
      </Field>

      {muestras.data && muestras.data.length > 0 && (
        <Field label="Muestra (opcional)">
          <select
            value={form.muestraId}
            onChange={(e) => setForm((f) => ({ ...f, muestraId: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          >
            <option value="">— sin vincular —</option>
            {muestras.data.map((m) => (
              <option key={m.id} value={m.id}>
                {m.folio} ({m.estado})
              </option>
            ))}
          </select>
        </Field>
      )}

      <div className="grid gap-2 sm:grid-cols-3">
        <Field label="Fecha de emisión *">
          <input
            type="date"
            value={form.fechaEmision}
            onChange={(e) => setForm((f) => ({ ...f, fechaEmision: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Vigencia desde">
          <input
            type="date"
            value={form.vigenciaInicio}
            onChange={(e) => setForm((f) => ({ ...f, vigenciaInicio: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Vigencia hasta">
          <input
            type="date"
            value={form.vigenciaFin}
            onChange={(e) => setForm((f) => ({ ...f, vigenciaFin: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
      </div>

      <Field label="Resultado *">
        <select
          value={form.resultado}
          onChange={(e) => setForm((f) => ({ ...f, resultado: e.target.value }))}
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        >
          <option value="">Selecciona…</option>
          <option value="CUMPLE">CUMPLE</option>
          <option value="NO_CUMPLE">NO CUMPLE</option>
          <option value="CONDICIONADO">CONDICIONADO</option>
          <option value="INCONCLUSO">INCONCLUSO</option>
        </select>
      </Field>

      <Field label="Descripción del producto *">
        <input
          value={form.productoDescripcion}
          onChange={(e) => setForm((f) => ({ ...f, productoDescripcion: e.target.value }))}
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        />
      </Field>

      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Marca *">
          <input
            value={form.marca}
            onChange={(e) => setForm((f) => ({ ...f, marca: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Modelo *">
          <input
            value={form.modelo}
            onChange={(e) => setForm((f) => ({ ...f, modelo: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
      </div>

      <Field label="Observaciones de captura">
        <textarea
          rows={2}
          value={form.observaciones}
          onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))}
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        />
      </Field>

      {error && <ErrorBox text={error} />}
      <Footer
        onCancel={onClose}
        onConfirm={() => {
          const faltantes: string[] = [];
          if (!form.numeroReporte.trim()) faltantes.push("número");
          if (!form.parId) faltantes.push("laboratorio");
          if (!form.fechaEmision) faltantes.push("fecha de emisión");
          if (!form.resultado) faltantes.push("resultado");
          if (!form.productoDescripcion.trim()) faltantes.push("descripción");
          if (!form.marca.trim()) faltantes.push("marca");
          if (!form.modelo.trim()) faltantes.push("modelo");
          if (faltantes.length) {
            setError(`Faltan campos: ${faltantes.join(", ")}.`);
            return;
          }
          setError(null);
          crear.mutate();
        }}
        confirmLabel="Capturar"
        loading={crear.isPending}
      />
    </Modal>
  );
}

function AccionModal({
  rp,
  accion,
  onClose,
  onSuccess,
}: {
  rp: ReportePrueba;
  accion: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [body, setBody] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const config = configurar(accion);

  const ejecutar = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {};
      Object.entries(body).forEach(([k, v]) => {
        if (v.trim()) payload[k] = v.trim();
      });
      await api.post(
        `/api/sector/telecom/reportes-prueba/${rp.id}/${accion}`,
        Object.keys(payload).length ? payload : {},
      );
    },
    onSuccess,
    onError: (err: AxiosError<{ detail?: string }>) =>
      setError(err.response?.data?.detail ?? "Error en la operación"),
  });

  return (
    <Modal title={`${config.titulo} — ${rp.folioInterno}`} onClose={onClose}>
      {config.descripcion && <p className="text-sm text-fg-muted">{config.descripcion}</p>}
      {config.fields.map((f) => (
        <Field key={f.name} label={`${f.label}${f.required ? " *" : ""}`}>
          {f.tipo === "textarea" ? (
            <textarea
              rows={3}
              value={body[f.name] ?? ""}
              onChange={(e) => setBody((b) => ({ ...b, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          ) : (
            <input
              value={body[f.name] ?? ""}
              onChange={(e) => setBody((b) => ({ ...b, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          )}
        </Field>
      ))}
      {error && <ErrorBox text={error} />}
      <Footer
        onCancel={onClose}
        onConfirm={() => {
          for (const f of config.fields) {
            if (f.required && !(body[f.name] ?? "").trim()) {
              setError(`Falta: ${f.label}`);
              return;
            }
          }
          setError(null);
          ejecutar.mutate();
        }}
        confirmLabel={config.titulo}
        loading={ejecutar.isPending}
      />
    </Modal>
  );
}

type FieldSpec = {
  name: string;
  label: string;
  required?: boolean;
  tipo?: "text" | "textarea";
  placeholder?: string;
};

function configurar(accion: string): { titulo: string; descripcion?: string; fields: FieldSpec[] } {
  switch (accion) {
    case "aceptar":
      return {
        titulo: "Aceptar",
        descripcion: "Marca el RP como válido para la decisión técnica. Si el RP venció no podrá aceptarse.",
        fields: [],
      };
    case "rechazar":
      return {
        titulo: "Rechazar",
        descripcion: "El RP no procede. El estatus pasa a RECHAZADO y no puede revertirse.",
        fields: [{ name: "motivo", label: "Motivo del rechazo", required: true, tipo: "textarea" }],
      };
    case "observar":
      return {
        titulo: "Observar",
        descripcion: "Solicita corrección al laboratorio o al tramitador. El RP queda en OBSERVADO.",
        fields: [
          {
            name: "observaciones",
            label: "Observaciones",
            required: true,
            tipo: "textarea",
            placeholder: "Describe qué se debe corregir",
          },
        ],
      };
    case "subsanar":
      return {
        titulo: "Subsanar",
        descripcion: "Captura las aclaraciones del laboratorio o tramitador. El RP vuelve a CAPTURADO para nueva revisión.",
        fields: [{ name: "aclaraciones", label: "Aclaraciones", required: true, tipo: "textarea" }],
      };
    case "vincular-documento":
      return {
        titulo: "Vincular PDF",
        descripcion: "Pega el identificador del documento ya cargado en el módulo documental. En una próxima iteración este modal subirá el PDF directamente.",
        fields: [
          {
            name: "documentoId",
            label: "ID del documento",
            required: true,
            placeholder: "uuid del documento",
          },
        ],
      };
    default:
      return { titulo: accion, fields: [] };
  }
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl space-y-3 rounded-lg bg-surface p-4 shadow-overlay">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold text-fg">{title}</h3>
          <button onClick={onClose} className="cursor-pointer text-fg-faint transition hover:text-fg-muted">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-fg-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded border border-estado-err/25 bg-estado-err-bg p-2 text-sm text-estado-err">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {text}
    </div>
  );
}

function Footer({
  onCancel,
  onConfirm,
  confirmLabel,
  loading,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  loading?: boolean;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        onClick={onCancel}
        className="cursor-pointer rounded border bg-surface px-3 py-1.5 text-sm transition hover:bg-surface-2"
      >
        Cancelar
      </button>
      <button
        disabled={loading}
        onClick={onConfirm}
        className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
      >
        {confirmLabel}
      </button>
    </div>
  );
}
